/**
 * B7 panel-prefs client (design rev F §4) — Pipeline B's OWN client stack.
 *
 * SEPARATION: imports NOTHING from the Pipeline A client path and nothing from the
 * Flask prefs client (voiceAudioPrefs.ts is vestigial — the store is B-owned, both
 * ends). The socket connects with role:"panel": it is a SUBSCRIBER — it cannot
 * begin, preserve, or end a voice session, and its close is invisible to a call.
 *
 * Messages: get_prefs / set_prefs(patch) / list_input_devices, request/response
 * correlated by req_id, each returning {prefs, effective} from the applying
 * authority. state_changed is the contentless nudge — re-read, nothing more.
 */

export interface PanelPrefs {
  sensitivity_mode: 'default' | 'manual';
  sensitivity: number;               // canonical: stored = effective = displayed
  noise_suppression: boolean;
  echo_cancel: boolean;
  input_device: string | null;       // null = the daemon's boot candidate order
}

export interface EffectiveState {
  front: 'full' | 'degraded';
  denoiser: 'up' | 'down' | 'disabled';
  aec: 'on' | 'bypassed';            // the L2-APPLIED flag, never the pref
  threshold: number;                 // the number L2 is actually using
  sensitivity_mode: string;          // the L2-APPLIED mode (Ghost F2)
  noise_gate: boolean;               // the consumer's actual gate value
  input_device: {
    actual: string | null;           // the device L1 actually holds open
    requested: string | null;
    error: { requested: string; error: string } | null;
  };
}

export interface PrefsSnapshot { prefs: PanelPrefs; effective: EffectiveState; }
export interface InputDevice { id: string; description: string; }

export type PanelPrefsPatch = Partial<PanelPrefs>;

interface RexyElectronBridge {
  getVoiceDaemonNonce: () => Promise<{ success: boolean; nonce?: string; port?: number }>;
}

interface PanelSocketOpts {
  onStateChanged: () => void;        // the nudge: caller re-reads via getPrefs()
  onClose: () => void;
}

export async function connectPanelPrefs(opts: PanelSocketOpts): Promise<PanelPrefsSocket> {
  const bridge = (window as unknown as { rexyElectron?: RexyElectronBridge }).rexyElectron;
  if (!bridge?.getVoiceDaemonNonce) {
    throw new Error('voice daemon is desktop-only (no Electron bridge)');
  }
  const ctl = await bridge.getVoiceDaemonNonce();
  if (!ctl.success || !ctl.nonce) {
    throw new Error('voice daemon control file unavailable — is the daemon up?');
  }
  return new PanelPrefsSocket(ctl.nonce, ctl.port ?? 8765, opts);
}

interface PendingRequest {
  resolve: (msg: Record<string, unknown>) => void;
  reject: (err: Error) => void;
}

export class PanelPrefsSocket {
  private ws: WebSocket;
  private opts: PanelSocketOpts;
  private nextReq = 1;
  private pending = new Map<number, PendingRequest>();
  private opened: Promise<void>;

  constructor(nonce: string, port: number, opts: PanelSocketOpts) {
    this.opts = opts;
    this.ws = new WebSocket(`ws://127.0.0.1:${port}`);
    this.opened = new Promise((resolve, reject) => {
      this.ws.onopen = () => {
        this.ws.send(JSON.stringify({ nonce, role: 'panel' }));
        resolve();
      };
      this.ws.onerror = () => reject(new Error('panel socket failed'));
    });
    // T1 fix (rev B.1): the handled-marker. An ABANDONED socket (React 18
    // StrictMode double-mounts effects; the first mount's socket is closed
    // before any request awaits `opened`) would otherwise reject with zero
    // handlers — a window-level unhandledrejection that red-screens the whole
    // OS when the daemon is absent, which is the app's DESIGNED DEFAULT state.
    // This branch observes the rejection at the source; every later
    // `await this.opened` in request() still sees it (a branch, not a swallow).
    this.opened.catch(() => {});
    this.ws.onmessage = (m) => {
      let msg: Record<string, unknown>;
      try { msg = JSON.parse(m.data as string); } catch { return; }
      if (msg.type === 'state_changed') { this.opts.onStateChanged(); return; }
      const reqId = msg.req_id as number | undefined;
      if (reqId !== undefined && this.pending.has(reqId)) {
        const p = this.pending.get(reqId)!;
        this.pending.delete(reqId);
        p.resolve(msg);
      }
    };
    this.ws.onclose = () => {
      // Ghost JOINT finding 4: every in-flight request REJECTS on close — a
      // cleared-but-unrejected promise left the panel pending (and every control
      // disabled) forever. The component's .catch/.finally recover the UI.
      const inflight = Array.from(this.pending.values());
      this.pending.clear();
      for (const p of inflight) p.reject(new Error('panel socket closed'));
      this.opts.onClose();
    };
  }

  private async request(cmd: string, extra: object = {}): Promise<Record<string, unknown>> {
    await this.opened;
    if (this.ws.readyState !== WebSocket.OPEN) throw new Error('panel socket closed');
    const req_id = this.nextReq++;
    const reply = new Promise<Record<string, unknown>>((resolve, reject) => {
      this.pending.set(req_id, { resolve, reject });
      setTimeout(() => {
        const p = this.pending.get(req_id);
        if (p) { this.pending.delete(req_id); p.reject(new Error('panel request timed out')); }
      }, 5000);
    });
    this.ws.send(JSON.stringify({ cmd, req_id, ...extra }));
    return reply;
  }

  async getPrefs(): Promise<PrefsSnapshot> {
    const m = await this.request('get_prefs');
    return { prefs: m.prefs as PanelPrefs, effective: m.effective as EffectiveState };
  }

  async setPrefs(patch: PanelPrefsPatch): Promise<PrefsSnapshot> {
    const m = await this.request('set_prefs', { patch });
    return { prefs: m.prefs as PanelPrefs, effective: m.effective as EffectiveState };
  }

  async listInputDevices(): Promise<InputDevice[]> {
    const m = await this.request('list_input_devices');
    return (m.devices as InputDevice[]) ?? [];
  }

  close() { this.ws.close(); }
}
